import {BlockMap} from 'UI/modules/Blocks';

import {isDefined} from 'GlobalShared/utils/typeGuards';

import type {Config} from 'types/payload-types';

export type BlockUnionType = Config['blocks'][keyof Config['blocks']];

/**
 * The `useBlockRenderer` hook transforms an array of Payload CMS block data into renderable components with their associated data.
 * This hook extracts the `blockType` from each block and maps it to the corresponding React component from the BlockMap.
 * It handles null and undefined content gracefully by returning an empty blocks array when no content is provided.
 *
 * @param content An array of block union types from Payload CMS configuration, or null/undefined if no blocks exist.
 * @returns An object containing the transformed blocks array with Component and data properties, or undefined if no content provided.
 *
 * @example
 * ```tsx
 * const {blocks} = useBlockRenderer(pageContent?.layout);
 *
 * return (
 *   <div>
 *     {blocks?.map((block, index) => (
 *       <block.Component key={index} {...block.data} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export const useBlockRenderer = (content: (BlockUnionType)[] | null | undefined) => {
    let blocks;

    if (isDefined(content)) {
        blocks = content.map(block => {
            const {blockType} = block;

            return {
                Component: BlockMap[blockType],
                data: block
            };
        });
    }

    return {blocks};
};